// ABOUTME: Workspace feature overrides section for application admins.
// ABOUTME: Shows plan feature defaults and allows toggling overrides per workspace.

import { useEffect, useState, useCallback } from 'react';
import { ToggleLeft, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import { limitOverrideApi } from '@/services/api/adminClient';
import type { EffectiveLimitsResponse } from '@/types/admin';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';

interface FeatureOverridesSectionProps {
  workspaceId: string;
}

const FEATURE_FIELDS: { key: string; label: string; description: string }[] = [
  { key: 'allow_password_sharing', label: 'Password Protection', description: 'Allow password-protected sharing' },
  { key: 'allow_email_sharing', label: 'Email Verification', description: 'Allow email-gated sharing' },
  { key: 'allow_comments', label: 'Comments', description: 'Allow comments on shared reports' },
  { key: 'require_branding', label: 'Require Branding', description: 'Force "Powered by Oleum BI" on shared reports' },
  { key: 'allow_self_service_data_config', label: 'Self-Service Data Config', description: 'Allow self-service data source configuration (CSV upload, etc.)' },
];

export function FeatureOverridesSection({ workspaceId }: FeatureOverridesSectionProps) {
  const [data, setData] = useState<EffectiveLimitsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [localOverrides, setLocalOverrides] = useState<Record<string, boolean>>({});
  const [hasChanges, setHasChanges] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await limitOverrideApi.getEffective(workspaceId);
      setData(result);
      setLocalOverrides(result.feature_overrides || {});
      setHasChanges(false);
    } catch {
      toast.error('Failed to load feature overrides');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleToggle = (key: string, value: boolean) => {
    setLocalOverrides((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleClearOverride = (key: string) => {
    setLocalOverrides((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await limitOverrideApi.updateFeatures(workspaceId, localOverrides);
      setData(result);
      setLocalOverrides(result.feature_overrides || {});
      setHasChanges(false);
      toast.success('Feature overrides applied successfully');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save feature overrides');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (data) {
      setLocalOverrides(data.feature_overrides || {});
      setHasChanges(false);
    }
  };

  if (loading) {
    return (
      <Card variant="surface">
        <div className="flex items-center justify-center h-24">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
        </div>
      </Card>
    );
  }

  if (!data) return null;

  return (
    <Card variant="surface">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ToggleLeft className="w-5 h-5 text-surface-500" />
          <h3 className="text-sm font-medium text-surface-800">Feature Overrides</h3>
          <span className="text-xs text-surface-500">Plan: {data.plan_name}</span>
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <Button variant="ghost" size="sm" onClick={handleDiscard}>
              Discard
            </Button>
          )}
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!hasChanges}
            isLoading={saving}
          >
            <Save className="w-3.5 h-3.5 mr-1" />
            Save
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-surface-200">
              <th className="text-left py-2 px-3 text-xs font-medium text-surface-500 uppercase tracking-wider">Feature</th>
              <th className="text-center py-2 px-3 text-xs font-medium text-surface-500 uppercase tracking-wider">Plan Default</th>
              <th className="text-center py-2 px-3 text-xs font-medium text-surface-500 uppercase tracking-wider">Override</th>
              <th className="text-center py-2 px-3 text-xs font-medium text-surface-500 uppercase tracking-wider">Effective</th>
            </tr>
          </thead>
          <tbody>
            {FEATURE_FIELDS.map(({ key, label, description }) => {
              const planDefault = (data.plan_features as unknown as Record<string, boolean>)[key];
              const hasOverride = key in localOverrides;
              const overrideValue = localOverrides[key];
              const effectiveValue = hasOverride ? overrideValue : planDefault;

              return (
                <tr key={key} className="border-b border-surface-100">
                  <td className="py-3 px-3">
                    <p className="font-medium text-surface-800">{label}</p>
                    <p className="text-xs text-surface-500">{description}</p>
                  </td>
                  <td className="py-3 px-3 text-center">
                    <BoolBadge value={planDefault} />
                  </td>
                  <td className="py-3 px-3 text-center">
                    {hasOverride ? (
                      <div className="flex items-center justify-center gap-2">
                        <BoolBadge value={overrideValue} highlighted />
                        <button
                          onClick={() => handleClearOverride(key)}
                          className="text-xs text-surface-400 hover:text-error"
                          title="Clear override (use plan default)"
                        >
                          &times;
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-surface-400">--</span>
                    )}
                  </td>
                  <td className="py-3 px-3 text-center">
                    <button
                      onClick={() => handleToggle(key, !effectiveValue)}
                      className="cursor-pointer"
                      title={`Click to ${effectiveValue ? 'disable' : 'enable'}`}
                    >
                      <BoolBadge value={effectiveValue} highlighted={hasOverride} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function BoolBadge({ value, highlighted }: { value: boolean; highlighted?: boolean }) {
  if (value) {
    return (
      <span className={`inline-block px-2 py-0.5 text-xs rounded-sm ${
        highlighted ? 'bg-primary-100 text-primary-700 font-medium' : 'bg-success-light text-success'
      }`}>
        Yes
      </span>
    );
  }
  return (
    <span className={`inline-block px-2 py-0.5 text-xs rounded-sm ${
      highlighted ? 'bg-primary-100 text-primary-700 font-medium' : 'bg-surface-100 text-surface-500'
    }`}>
      No
    </span>
  );
}
